import { ItemId } from "@atlaskit/tree"
import { useMemo } from "react"
import { knowledgeBaseApi } from "../../../api/controllers/knowledgeBase/knowledgeBase"
import { useGetQueueExtendedSettingsQuery } from "../../../api/controllers/queues"
import { useGetDialogApiCachedEndpointData } from "../../../api/selectors"
import { useProjectSettingCheck } from "../../../hooks/useProjectSettingCheck"
import { Article } from "../../../models/article"
import { CategoryTreeData, CategoryTreeItem } from "../../../models/category"
import { ResponseTemplateOperatorProjectSettings } from "../../../models/projectSettings"
import { selectDialogId } from "../../../store/dialogs/selectors"
import { useAppSelector } from "../../../store/hooks"
import { selectCatalog } from "../../../store/knowledgeBase/selectors"
import { selectCurrentProjectId } from "../../../store/projects/selectors"
import { RootState } from "../../../store/store"

export enum FastRepliesDataType {
    ARTICLES = "articles",
    ALL = "all"
}
type ArticlesResult = {
    articleNodes: CategoryTreeItem[]
}

type CatalogWithArticlesResult = ArticlesResult & {
    catalogId: string
    articlesIds: string[]
    tree?: CategoryTreeData
}

export function useFastRepliesData(type: FastRepliesDataType.ARTICLES): ArticlesResult
export function useFastRepliesData(type: FastRepliesDataType.ALL): CatalogWithArticlesResult
export function useFastRepliesData(type: FastRepliesDataType) {
    const selectedDialogId = useAppSelector(selectDialogId)
    const currentProjectId = useAppSelector(selectCurrentProjectId)
    const dialogData = useGetDialogApiCachedEndpointData("getDialog", selectedDialogId ?? "")?.data

    const isActiveDialog = Boolean(selectedDialogId)
    const queueId = dialogData?.Queue?.Id ?? ""
    const dialogProjectId = dialogData?.Project?.Id
    const projectId = dialogProjectId || currentProjectId || ""
    const isArticlesOnly = type === FastRepliesDataType.ARTICLES

    const fastReplyCatalogId =
        useProjectSettingCheck<ResponseTemplateOperatorProjectSettings, "FastReplyTemplates">(
            "FastReplyTemplates",
            projectId
        ) || ""

    const { data: queueExtendedSettings } = useGetQueueExtendedSettingsQuery(
        {
            projectId,
            queueId
        },
        { skip: !projectId || !queueId || !isActiveDialog }
    )

    const catalogId = (isActiveDialog && queueExtendedSettings?.KnowledgeBase?.CatalogId) || fastReplyCatalogId
    const { data: articlesData } = useAppSelector(
        knowledgeBaseApi.endpoints.getAllPublishInCatalog.select({
            body: { CatalogCode: catalogId, Login: "" },
            projectId: ""
        })
    ) // Для селекта, нам нужен только CatalogCode, остальные параметры не нужны
    const catalogTree = useAppSelector((state: RootState) => selectCatalog(state, catalogId))

    const { articleNodes, articlesIds } = useMemo(
        () => ({
            articleNodes:
                articlesData?.map(article => ({
                    id: article.Id,
                    children: [],
                    data: {
                        parentId: "",
                        status: article.Status,
                        title: article.Title,
                        symbolCode: article.SymbolCode,
                        catalogCode: "",
                        permissions: article.Permissions,
                        permissionsWithNested: true,
                        newsline: false,
                        answer: article.Answers?.[0]?.Text ?? ""
                    }
                })) ?? [],
            articlesIds: isArticlesOnly ? [] : articlesData?.map(article => article.SymbolCode) ?? []
        }),
        [articlesData, isArticlesOnly]
    )

    const enrichedTree = useMemo(() => {
        if (!catalogTree || isArticlesOnly) return undefined

        const bySymbol = new Map<string, Article>()
        for (const a of articlesData ?? []) {
            if (a.SymbolCode) bySymbol.set(a.SymbolCode, a)
        }

        if (bySymbol.size === 0) return catalogTree

        const newItems: Record<string, CategoryTreeItem> = {}

        const cloneNode = (id: ItemId) => {
            const item = catalogTree.items[id]
            if (!item) return

            const article = bySymbol.get(item.data.symbolCode)

            const patchedData = article
                ? {
                      ...item.data,
                      answer: article.Answers?.[0]?.Text ?? item.data.answer ?? ""
                  }
                : item.data

            const clonedChildren = item.children.slice()
            for (const childId of clonedChildren) cloneNode(childId)

            newItems[id] = {
                ...item,
                children: clonedChildren,
                data: patchedData
            }
        }

        cloneNode(catalogTree.rootId)

        return { rootId: catalogTree.rootId, items: newItems }
    }, [catalogTree, articlesData, isArticlesOnly])

    if (isArticlesOnly) {
        const fastRepliesData: ArticlesResult = {
            articleNodes
        }
        return fastRepliesData
    } else {
        const fastRepliesData: CatalogWithArticlesResult = {
            catalogId,
            articleNodes,
            articlesIds: articlesIds,
            tree: enrichedTree
        }
        return fastRepliesData
    }
}
